<template>
  <div v-if="shouldShow && relatedArticles?.length" class="mt-12">
    <h2 class="mb-12 text-center font-headline text-h2">{{ title }}</h2>
    <div
      class="flex flex-wrap justify-center gap-6"
      :class="{
        'sm:justify-center': relatedArticles.length <= 2,
        'sm:justify-start': relatedArticles.length >= 3,
      }"
    >
      <content-card
        v-for="article in relatedArticles"
        :key="article.sys.id"
        :article="article"
        class="h-full"
        :class="{
          'w-full max-w-[468px]': relatedArticles?.length === 1,
          'w-full max-w-[468px] sm:w-[calc(50%-0.75rem)]': relatedArticles?.length === 2,
          'w-full sm:w-[calc(50%-0.75rem)] md:w-[calc(33.333%-1rem)]': (relatedArticles?.length ?? 0) >= 3,
        }"
        :contain-image="(article as any)?.fields?.imageOtherAspect === true"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Article } from '@/types/contentful'
import { generateArticlePath } from '@/utils/slugResolver'

interface Props {
  currentArticle: Article
}

const props = defineProps<Props>()

const route = useRoute()

// Determine if we should show related articles (only for specific pages)
const shouldShow = computed(() => {
  const path = route.path
  return (
    path.startsWith('/hjalp-djuren/') ||
    path.startsWith('/djurfakta/') ||
    path.startsWith('/vuxen/') ||
    path.startsWith('/klubbnytt/') ||
    path.startsWith('/om-oss/')
  )
})

// Get the appropriate title for related articles based on the page
const title = computed(() => {
  const path = route.path
  if (path.startsWith('/hjalp-djuren/')) {
    return 'Fler sätt att hjälpa djuren'
  } else if (path.startsWith('/djurfakta/')) {
    return 'Lär dig mer om dessa djur'
  } else if (path.startsWith('/vuxen/')) {
    return 'Fler artiklar att läsa'
  } else if (path.startsWith('/klubbnytt/')) {
    return 'Läs våra senaste nummer'
  } else if (path.startsWith('/om-oss/')) {
    return 'Lär dig mer om klubben'
  }
  return 'Relaterade artiklar'
})

const { getArticlesByParent, getArticlesByCategory } = useContentful()

// Cache key based on current article identity
const articleId = computed(() => props.currentArticle?.sys?.id || 'none')

// Use useAsyncData with watch for proper SSR caching + client reactivity
const { data: relatedArticles } = await useAsyncData(
  `related:${articleId.value}`,
  async () => {
    if (!shouldShow.value || !props.currentArticle?.fields) return []

    try {
      let articlesResponse: any
      const parentId = props.currentArticle.fields.parent?.sys?.id

      if (parentId) {
        if ((props.currentArticle.fields as any)?.category?.sys?.id) {
          const categoryId = (props.currentArticle.fields as any).category.sys.id
          articlesResponse = await getArticlesByCategory(categoryId, {
            limit: 4,
            include: 2,
            order: '-fields.date',
          })
        } else {
          articlesResponse = await getArticlesByParent(parentId, {
            limit: 4,
            include: 2,
            order: '-fields.date',
          })
        }

        if (articlesResponse?.items) {
          const filtered = articlesResponse.items.filter(
            (article: Article) => article.sys.id !== props.currentArticle.sys.id,
          )

          return filtered.slice(0, 3).map((article: Article) => ({
            ...article,
            _path: generateArticlePath(article),
            includes: articlesResponse.includes,
          }))
        }
      }

      return []
    } catch (error) {
      console.error('Error fetching related articles:', error)
      return []
    }
  },
  {
    server: true,
    watch: [articleId], // Re-fetch when article changes during client navigation
  },
)
</script>
